import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, icsFeedUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronLeft, ChevronRight, Flame, Trophy, CalendarDays,
  LogOut, Download, Copy, Check, Sparkles, Crown, LifeBuoy, UserRound,
} from "lucide-react";
import { toast } from "sonner";
import LessonDialog from "@/components/LessonDialog";
import TodayHero from "@/components/TodayHero";
import RecapDialog from "@/components/RecapDialog";
import SupportDialog from "@/components/SupportDialog";
import ProfileDialog from "@/components/ProfileDialog";
import GetAppCard from "@/components/GetAppCard";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(year, monthIdx) {
  // returns 6*7 = 42 date objects starting from the first Sunday <= 1st of month
  const first = new Date(year, monthIdx, 1);
  const startOffset = first.getDay(); // 0..6 (Sun..Sat)
  const start = new Date(year, monthIdx, 1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export default function Dashboard() {
  const { user, logout, setLanguage } = useAuth();
  const nav = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [openDate, setOpenDate] = useState(null);
  const [openRecap, setOpenRecap] = useState(null); // ISO of the Friday of the week
  const [openSupport, setOpenSupport] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [copied, setCopied] = useState(false);

  const isPro = user?.plan === "pro" || user?.plan === "lifetime";

  useEffect(() => { api.get("/languages").then((r) => setLanguages(r.data)); }, []);

  const monthDays = useMemo(
    () => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  );
  const todayIso = isoDate(today);
  const completed = new Set(user?.completed_dates || []);
  const currentLang = languages.find((l) => l.code === user?.language);
  const feedUrl = icsFeedUrl(user?.subscription_token);

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const changeLanguage = async (code) => {
    await setLanguage(code);
    toast.success(`Switched to ${languages.find((l) => l.code === code)?.name}`);
  };

  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      toast.success("Calendar feed URL copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the link");
    }
  };

  return (
    <div className="min-h-screen grain" data-testid="dashboard">
      {/* Header */}
      <header className="border-b bg-card/60 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-terracotta" strokeWidth={1.75} />
            <span className="font-display text-xl">
              <span>Calen</span><span className="text-honey">Lingo</span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            {isPro ? (
              <div className="flex items-center gap-1.5 text-xs bg-forest text-white px-3 py-1 rounded-full" data-testid="pro-badge">
                <Crown className="w-3 h-3" strokeWidth={2.5} />
                {user.plan === "lifetime" ? "Lifetime" : "Pro"}
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => nav("/pricing")}
                data-testid="upgrade-button"
                className="rounded-full h-9 border-terracotta text-terracotta hover:bg-terracotta hover:text-white"
              >
                <Crown className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Upgrade
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full h-9" data-testid="language-switcher">
                  <span className="mr-2">{currentLang?.flag}</span>
                  {currentLang?.name || "Choose language"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Switch language</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    onClick={() => changeLanguage(l.code)}
                    data-testid={`switch-lang-${l.code}`}
                  >
                    <span className="mr-2">{l.flag}</span> {l.name}
                    <span className="ml-auto text-xs text-muted-foreground">{l.native}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost" size="sm" onClick={() => setOpenSupport(true)}
              data-testid="support-button"
              className="hidden sm:inline-flex"
              title="Contact support"
            >
              <LifeBuoy className="w-4 h-4 mr-2" strokeWidth={1.75} /> Support
            </Button>
            <Button
              variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Calendar */}
        <section className="space-y-6">
          {currentLang && (
            <TodayHero
              language={user.language}
              langLabel={currentLang.name}
              isCompleted={completed.has(todayIso)}
              onOpen={() => setOpenDate(todayIso)}
            />
          )}

          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm uppercase tracking-widest text-muted-foreground">Your month</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-1" data-testid="month-title">
                {MONTHS[cursor.getMonth()]} <span className="text-muted-foreground">{cursor.getFullYear()}</span>
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goPrev} data-testid="prev-month" className="rounded-full">
                <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
              </Button>
              <Button variant="outline" onClick={goToday} data-testid="today-button" className="rounded-full h-9">
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={goNext} data-testid="next-month" className="rounded-full">
                <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2 sm:gap-3">
            {DOW.map((d) => (
              <div key={d} className="text-xs uppercase tracking-widest text-muted-foreground py-2 text-center">
                {d}
              </div>
            ))}
            {monthDays.map((d, idx) => {
              const iso = isoDate(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = iso === todayIso;
              const isFuture = iso > todayIso;
              const isDone = completed.has(iso);
              const isFriday = d.getDay() === 5;
              const recapUnlocked = isFriday && !isFuture; // Friday of a past or current week
              const disabled = !inMonth;
              return (
                <button
                  key={idx}
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;
                    if (recapUnlocked) setOpenRecap(iso);
                    else setOpenDate(iso);
                  }}
                  data-testid={recapUnlocked ? `recap-cell-${iso}` : `day-cell-${iso}`}
                  className={[
                    "calendar-cell relative aspect-square sm:aspect-[4/5] rounded-xl bg-card p-2 sm:p-3 text-left flex flex-col justify-between",
                    disabled ? "opacity-30 pointer-events-none border border-dashed border-border" : "",
                    isToday ? "border-2 border-terracotta shadow-md" : "",
                    !isToday && !isFuture && !isDone && !recapUnlocked && inMonth ? "border border-border" : "",
                    !isToday && isFuture && inMonth ? "border border-dashed border-border" : "",
                    isDone && !recapUnlocked ? "border-2 border-forest bg-forest/5" : "",
                    recapUnlocked && inMonth ? "border-2 border-honey/70 bg-honey/10" : "",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between">
                    <span className={`text-sm font-semibold ${isToday ? "text-terracotta" : ""} ${recapUnlocked ? "text-honey" : ""}`}>
                      {d.getDate()}
                    </span>
                    {isDone && !recapUnlocked && (
                      <div className="bg-forest text-white rounded-full p-0.5">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                    )}
                    {recapUnlocked && inMonth && (
                      <div className="bg-honey text-secondary rounded-full p-0.5" title="Weekly recap">
                        <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                      </div>
                    )}
                  </div>
                  {inMonth && currentLang && (
                    <div className={`text-[10px] sm:text-xs leading-tight ${
                      recapUnlocked ? "text-honey" :
                      isFuture ? "text-muted-foreground" :
                      isDone ? "text-forest" : "text-foreground/70"
                    }`}>
                      <div className="flex items-center gap-1">
                        <span>{recapUnlocked ? "✨" : currentLang.flag}</span>
                        <span className="truncate hidden sm:inline">
                          {recapUnlocked ? "Weekly recap" :
                           isFuture ? "Coming up" :
                           isDone ? "Completed" : "Lesson ready"}
                        </span>
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Side panel */}
        <aside className="space-y-6" data-testid="side-panel">
          <div className="bg-card border rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest">
              <Flame className="w-4 h-4 text-terracotta" strokeWidth={1.75} /> Current streak
            </div>
            <div className="flex items-end justify-between">
              <div className="font-display text-6xl leading-none tracking-tight" data-testid="current-streak">
                {user?.streak || 0}
              </div>
              <div className="text-sm text-muted-foreground pb-2">
                {(user?.streak || 0) === 1 ? "day" : "days"}
              </div>
            </div>
            {(user?.streak || 0) > 0 && (
              <div
                className="text-xs text-terracotta font-medium"
                data-testid="streak-hype"
              >
                {(() => {
                  const s = user?.streak || 0;
                  if (s >= 30) return "Unstoppable. Truly.";
                  if (s >= 14) return "Two full weeks. The habit is yours.";
                  if (s >= 7) return "A whole week. Momentum is on.";
                  if (s >= 5) return "Five days deep — you're rolling!";
                  if (s >= 3) return "Three in a row. Keep it warm.";
                  return "The flame is lit. Feed it tomorrow.";
                })()}
              </div>
            )}
            <div className="pt-3 border-t border-border flex items-center justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" strokeWidth={1.75} /> Longest
              </span>
              <span className="font-medium" data-testid="longest-streak">{user?.longest_streak || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} /> Total days
              </span>
              <span className="font-medium" data-testid="total-days">{user?.completed_dates?.length || 0}</span>
            </div>
          </div>

          <div className="bg-secondary text-secondary-foreground rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm uppercase tracking-widest text-secondary-foreground/70">
              <CalendarDays className="w-4 h-4" strokeWidth={1.75} /> Sync to your calendar
            </div>
            <p className="text-sm text-secondary-foreground/80">
              Your daily word shows up right beside your meetings — like a friendly holiday.
            </p>
            <div className="space-y-2">
              <Button
                onClick={copyFeed}
                data-testid="copy-feed-button"
                className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-white"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied!" : "Copy subscription URL"}
              </Button>
              <a
                href={feedUrl} download={`lingo-${user?.language || "lessons"}.ics`}
                data-testid="download-ics-button"
              >
                <Button variant="outline" className="w-full rounded-full bg-transparent border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
                  <Download className="w-4 h-4 mr-2" /> Download .ics file
                </Button>
              </a>
            </div>
            <p className="text-xs text-secondary-foreground/60 leading-relaxed pt-2">
              In Outlook/Google Calendar &rarr; &ldquo;Add calendar from URL&rdquo; &rarr; paste the copied link. Updates every 12 hours.
            </p>
          </div>

          <div className="bg-card border rounded-2xl p-6 space-y-4" data-testid="profile-card">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  data-testid="profile-card-avatar-image"
                  referrerPolicy="no-referrer"
                  className="w-12 h-12 rounded-full object-cover border-2 border-honey shrink-0"
                />
              ) : (
                <div
                  data-testid="profile-card-avatar-initials"
                  className="w-12 h-12 rounded-full bg-terracotta text-white flex items-center justify-center font-display text-lg shrink-0 border-2 border-honey"
                >
                  {(user?.name || "?").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate" data-testid="user-name">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate" data-testid="user-email">{user?.email}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground border-t border-border pt-3 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <UserRound className="w-3.5 h-3.5" strokeWidth={1.75} />
                Member since <span className="text-foreground font-medium ml-1" data-testid="member-since-inline">
                  {(() => {
                    const c = user?.created_at;
                    if (!c) return "recently";
                    const d = new Date(c);
                    if (isNaN(d.getTime())) return "recently";
                    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
                  })()}
                </span>
              </span>
              <button
                onClick={() => setOpenProfile(true)}
                data-testid="view-profile-button"
                className="text-terracotta hover:underline"
              >
                View profile
              </button>
            </div>
          </div>

          <GetAppCard />
        </aside>
      </main>

      {openDate && (
        <LessonDialog
          isoDate={openDate}
          language={user.language}
          isPro={isPro}
          onClose={() => setOpenDate(null)}
          onUpgrade={() => { setOpenDate(null); nav("/pricing"); }}
        />
      )}
      {openRecap && (
        <RecapDialog
          language={user.language}
          isoFriday={openRecap}
          onClose={() => setOpenRecap(null)}
        />
      )}
      {openSupport && <SupportDialog onClose={() => setOpenSupport(false)} />}
      {openProfile && <ProfileDialog onClose={() => setOpenProfile(false)} />}
    </div>
  );
}
